import React, { useState } from 'react';
import { motion } from 'framer-motion';
import AccessoryCard from './AccessoryCard';
import AccessoryModal from '../modals/AccessoryModal';
import { accessories, getAccessoriesByCategory } from '../../data/accessories';

const AccessoriesSection = ({ 
  title = "Accessories & Components",
  subtitle = "Complete your installation with our professional-grade accessories",
  categories = ['adhesives', 'frames', 'ancillaries'],
  showCategoryTabs = true,
  maxItems = null,
  className = "",
  onRequestSample,
  onRequestQuote
}) => {
  const [selectedAccessory, setSelectedAccessory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(categories[0]);
  const [expandedCategory, setExpandedCategory] = useState(null);

  const handleCardClick = (accessory) => {
    setSelectedAccessory(accessory);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedAccessory(null), 200);
  };

  // Get accessories for the active category
  const displayAccessories = showCategoryTabs 
    ? getAccessoriesByCategory(activeCategory)
    : categories.flatMap(cat => getAccessoriesByCategory(cat));
  
  // Apply max items limit if specified (unless category is expanded)
  const limitedAccessories = (maxItems && expandedCategory !== activeCategory)
    ? displayAccessories.slice(0, maxItems) 
    : displayAccessories;

  const categoryLabels = {
    adhesives: 'Adhesives',
    frames: 'Aluminum Frames',
    ancillaries: 'Ancillaries'
  };

  return (
    <section className={`py-12 ${className}`}>
      <div className="container-portico">
        {/* Section Header */}
        <div className="text-center mb-8">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-heading font-bold text-gray-900 mb-2"
          >
            {title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-600"
          >
            {subtitle}
          </motion.p>
        </div>

        {/* Category Tabs */}
        {showCategoryTabs && categories.length > 1 && (
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-gray-100 rounded-lg p-1">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`
                    px-4 py-2 text-sm font-medium rounded-md transition-all
                    ${activeCategory === category 
                      ? 'bg-white text-primary shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                    }
                  `}
                  data-testid={`accessory-tab-${category}`}
                >
                  {categoryLabels[category]}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Accessories Grid */}
        <motion.div 
          layout
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
        >
          {limitedAccessories.map((accessory, index) => (
            <motion.div
              key={accessory.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
            >
              <AccessoryCard
                accessory={accessory}
                onClick={handleCardClick}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* View All Link (if limited) */}
        {maxItems && displayAccessories.length > maxItems && (
          <div className="text-center mt-8">
            <button 
              className="text-primary font-medium hover:underline"
              onClick={() => setExpandedCategory(prev => prev === activeCategory ? null : activeCategory)}
              data-testid={`view-all-${activeCategory}-btn`}
            >
              {expandedCategory === activeCategory 
                ? `Show Less ${categoryLabels[activeCategory]}`
                : `View All ${categoryLabels[activeCategory]} (${displayAccessories.length})`
              }
            </button>
          </div>
        )}
      </div>

      {/* Accessory Modal */}
      <AccessoryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        accessory={selectedAccessory}
        onRequestSample={onRequestSample}
        onRequestQuote={onRequestQuote}
      />
    </section>
  );
};

export default AccessoriesSection;
